import { ACTIVITY_EVENTS, DEFAULT_IDLE_TIMEOUT } from '../constants/activity-events.constant';

export interface IdleListenerOptions {
  timeout?: number;
  onIdle?: () => void;
  onActivity?: () => void;
}

export class IdleListener {
  private idleTimer: any;
  private readonly timeout: number;
  private readonly onIdle: () => void;
  private readonly onActivity: () => void;
  private readonly document: Document;
  private readonly boundResetTimer: () => void;

  constructor(document: Document, options: IdleListenerOptions = {}) {
    this.document = document;
    this.timeout = options.timeout || DEFAULT_IDLE_TIMEOUT;
    this.onIdle = options.onIdle || (() => {});
    this.onActivity = options.onActivity || (() => {});
    this.boundResetTimer = this.resetTimer.bind(this);
  }

  /**
   * Initialize the idle listener by adding event listeners
   */
  initialize(): void {
    // Add event listeners for all activity events
    ACTIVITY_EVENTS.forEach(event => {
      this.document.addEventListener(event, this.boundResetTimer, true);
    });

    // Start the timer
    this.resetTimer();
  }

  /**
   * Reset the idle timer and trigger activity callback
   */
  private resetTimer(): void {
    // Trigger activity callback
    this.onActivity();
    
    // Clear existing timer
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
    }
    
    // Set new timer
    this.idleTimer = setTimeout(() => {
      this.onIdle();
    }, this.timeout);
  }

  /**
   * Clean up event listeners and timers
   */
  destroy(): void {
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
    }
    
    // Remove event listeners
    ACTIVITY_EVENTS.forEach(event => {
      this.document.removeEventListener(event, this.boundResetTimer, true);
    });
  }
}

/**
 * Simple function to create and initialize an idle listener
 */
export function createIdleListener(
  document: Document, 
  options: IdleListenerOptions = {}
): IdleListener {
  const listener = new IdleListener(document, options);
  listener.initialize();
  return listener;
}
